import React, { useContext } from "react";
import { Trash, Plus } from "lucide-react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import GraphConfigFormContext from "@/store/contexts/GraphConfigFormContext";

interface ReferenceArea {
  y1: number;
  y2: number;
  label: string;
  color: string;
  axis: "left" | "right";
}

interface ReferenceAreaSelectorProps {}

const ReferenceAreaSelector = ({}: ReferenceAreaSelectorProps) => {
  const { config, setConfig } = useContext(GraphConfigFormContext);

  if (!config) return null;

  const referenceAreas = config.graph_options.referenceAreas || [];

  const onChange = (newReferenceAreas: ReferenceArea[]) => {
    setConfig({
      ...config,
      graph_options: {
        ...config.graph_options,
        referenceAreas: newReferenceAreas,
      },
    });
  };
  // Add a new reference area
  const handleAddReferenceArea = () => {
    const newReferenceArea: ReferenceArea = {
      y1: 0,
      y2: 10,
      label: "",
      color: "#B3E5FC",
      axis: "left",
    };
    onChange([...referenceAreas, newReferenceArea]);
  };

  // Remove a reference area
  const handleRemoveReferenceArea = (index: number) => {
    const newReferenceAreas = [...referenceAreas];
    newReferenceAreas.splice(index, 1);
    onChange(newReferenceAreas);
  };

  // Update a reference area
  const handleUpdateReferenceArea = (
    index: number,
    field: keyof ReferenceArea,
    value: any
  ) => {
    const newReferenceAreas = [...referenceAreas];
    newReferenceAreas[index] = {
      ...newReferenceAreas[index],
      [field]: value,
    };
    onChange(newReferenceAreas);
  };

  return (
    <div className="flex flex-col gap-4">
      <Label>Reference Areas</Label>

      {referenceAreas.length === 0 && (
        <p className="text-sm text-muted-foreground">
          No reference areas defined. Add one below.
        </p>
      )}

      {referenceAreas.map((area, index) => (
        <div key={index} className="flex flex-col gap-3 p-4 border rounded-md">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-medium">
              Reference Area {index + 1}
            </Label>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8"
              onClick={() => handleRemoveReferenceArea(index)}
            >
              <Trash className="h-4 w-4 text-destructive" />
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Y1 Value (From)</Label>
              <Input
                type="number"
                value={area.y1}
                onChange={(e) =>
                  handleUpdateReferenceArea(
                    index,
                    "y1",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-xs">Y2 Value (To)</Label>
              <Input
                type="number"
                value={area.y2}
                onChange={(e) =>
                  handleUpdateReferenceArea(
                    index,
                    "y2",
                    parseFloat(e.target.value) || 0
                  )
                }
                className="mt-1"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs">Label</Label>
            <Input
              type="text"
              value={area.label}
              onChange={(e) =>
                handleUpdateReferenceArea(index, "label", e.target.value)
              }
              className="mt-1"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Color</Label>
              <div className="flex items-center gap-2 mt-1">
                <Input
                  type="color"
                  value={area.color}
                  onChange={(e) =>
                    handleUpdateReferenceArea(index, "color", e.target.value)
                  }
                  className="w-10 h-10 p-1"
                />
                <Input
                  type="text"
                  value={area.color}
                  onChange={(e) =>
                    handleUpdateReferenceArea(index, "color", e.target.value)
                  }
                  className="flex-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-xs pb-1">Axis</Label>
              <Select
                value={area.axis}
                onValueChange={(value: "left" | "right") =>
                  handleUpdateReferenceArea(index, "axis", value)
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select axis" />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="left">Left</SelectItem>
                    <SelectItem value="right">Right</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      ))}

      <Button
        variant="outline"
        className="w-full mt-2"
        onClick={handleAddReferenceArea}
      >
        <Plus className="h-4 w-4 mr-2" /> Add Reference Area
      </Button>
    </div>
  );
};

export default ReferenceAreaSelector;
